import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { form, FormField, min, required, schema, submit } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { firstValueFrom } from 'rxjs';
import { BudgetUpdateResultDto, MceApiService, SubscriptionDto } from '../../core/mceapi.client.generated';
import { DialogService } from '../../shared/dialogs/dialog.service';

interface CreditAdjustmentFormModel {
  creditGbp: number | null;
  description: string;
}

interface PaygBudgetFormModel {
  limitGbp: number | null;
}

const creditAdjustmentFormSchema = schema<CreditAdjustmentFormModel>((path) => {
  required(path.creditGbp, { message: 'Account credit amount is required' });
  min(path.creditGbp, 0.01, { message: 'Account credit amount must be greater than zero' });
  required(path.description, { message: 'Adjustment reason is required' });
});

const paygBudgetFormSchema = schema<PaygBudgetFormModel>((path) => {
  required(path.limitGbp, { message: 'PAYG budget limit is required' });
  min(path.limitGbp, 0, { message: 'PAYG budget limit cannot be negative' });
});

@Component({
  selector: 'mce-support-user-stripe',
  imports: [FormField, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule],
  templateUrl: './support-user-stripe.component.html',
  styles: ``,
})
export class SupportUserStripeComponent {

  readonly userUid = input.required<string>();
  readonly subscription = input.required<SubscriptionDto | undefined>();
  readonly budgetUpdated = output<BudgetUpdateResultDto>();

  private readonly mceApiService = inject(MceApiService);
  private readonly dialogService = inject(DialogService);

  private readonly balanceResource = rxResource({
    params: () => ({ userUid: this.userUid() }),
    stream: ({ params }) => this.mceApiService.v1SubscriptionsBalanceUser({
      userUid: params.userUid,
    }),
  });

  protected readonly creditAdjustmentModel = signal<CreditAdjustmentFormModel>({
    creditGbp: null,
    description: '',
  });
  protected readonly creditAdjustmentForm = form<CreditAdjustmentFormModel>(this.creditAdjustmentModel, creditAdjustmentFormSchema);

  protected readonly paygBudgetModel = linkedSignal<PaygBudgetFormModel>(() => ({
    limitGbp: this.subscription()?.paygBudget ?? null,
  }));
  protected readonly paygBudgetForm = form<PaygBudgetFormModel>(this.paygBudgetModel, paygBudgetFormSchema);

  protected readonly viewState = computed(() => {
    const balance = this.balanceResource.value()?.balance;
    const budget = this.subscription()?.paygBudget ?? 0;
    const budgetConsumed = this.subscription()?.paygBudgetConsumed ?? 0;

    return {
      balance,
      budget,
      budgetConsumed,
      showBalanceLoading: this.balanceResource.isLoading(),
      currentAccountCreditLabel: balance === undefined
        ? 'Loading...'
        : balance < 0
          ? this.formatCurrency(Math.abs(balance))
          : this.formatCurrency(0),
      balanceHint: balance === undefined
        ? 'Loading the current Stripe balance.'
        : balance < 0
          ? 'Available account credit on the Stripe customer.'
          : balance > 0
            ? `${this.formatCurrency(balance)} is currently owed.`
            : 'No account credit is currently applied.',
      paygBudgetLabel: this.formatCurrency(budget),
      paygConsumedLabel: this.formatCurrency(budgetConsumed),
    };
  });

  protected submitCreditAdjustment(event: Event) {
    event.preventDefault();

    submit(this.creditAdjustmentForm, async (formState) => {
      const creditGbp = formState().value().creditGbp;
      const description = formState().value().description.trim();

      if (creditGbp === null) return;

      const confirmed = await this.dialogService.confirmAsync(
        'Add account credit',
        `Apply ${this.formatCurrency(creditGbp)} account credit to this Stripe customer?`,
        '280px'
      );
      if (!confirmed) return;

      try {
        await firstValueFrom(this.mceApiService.v1SubscriptionsBalanceAdjust({
          userUid: this.userUid(),
          amount: -creditGbp,
          description,
        }));

        this.balanceResource.reload();
        this.creditAdjustmentModel.set({
          creditGbp: null,
          description: '',
        });
      } catch (error) {
        console.error('Error adjusting Stripe account credit', error);
        throw error;
      }
    });
  }

  protected submitPaygBudgetLimit(event: Event) {
    event.preventDefault();

    submit(this.paygBudgetForm, async (formState) => {
      const requestedLimitGbp = formState().value().limitGbp;

      if (requestedLimitGbp === null) return;

      const confirmed = await this.dialogService.confirmAsync(
        'Set PAYG budget limit',
        `Set this user's PAYG budget limit to ${this.formatCurrency(requestedLimitGbp)}?`,
        '280px'
      );
      if (!confirmed) return;

      try {
        const result = await firstValueFrom(this.mceApiService.v1BillingBudgetSetUser({
          userUid: this.userUid(),
          paygBudget: requestedLimitGbp,
        }));

        this.budgetUpdated.emit(result);
      } catch (error) {
        console.error('Error updating PAYG budget limit', error);
        throw error;
      }
    });
  }

  private formatCurrency(value: number) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
}
